<script setup lang="ts">
import { storeToRefs } from 'pinia';
import CashbackItem from '@/components/sidebar/menu/CashbackItem.vue';
import { useLoyaltyHubStore } from '@/stores/loyalty-hub';
import {
  CASHBACK_ITEMS as items,
  cashbackPostSlug,
} from '@/utils/cashback-items';
import { getAssetsUrlForCDN } from '@/utils/template';

// Lista de opções de cashback (a mesma do ?open=cashback / CashbackMenu, vinda
// de utils/cashback-items.ts; título em
// `account.wallet.cashback_options_title`), no
// formato do modal da ROLETINHA no estado disponível (`.rlt-screen` em
// components/roulette/RouletteGame.vue): painel #000717, topbar com a logo da
// Apostatudo centrada e o X à direita, e partículas azuis subindo no fundo.
//
// Duas diferenças deliberadas em relação à roletinha: o card NÃO é colado no
// rodapé (fica centrado, com os quatro cantos arredondados) e a topbar não tem o
// grupo da esquerda (⋮ + presente), porque aqui não há sub-telas.
const store = useLoyaltyHubStore();
const { isCashbackOpen } = storeToRefs(store);
const route = useRoute();

// Wordmark branco da Apostatudo — o mesmo arquivo da topbar da roletinha.
const logo = getAssetsUrlForCDN('/assets/images/roleta/logo.png');

// Partículas azuis, a MESMA cor da roletinha (o check-in é o dourado).
const ACCENT_COLOR = '#5AA9FF';

const visible = ref(false);
const sheetOpen = ref(false);

watch(
  isCashbackOpen,
  async (opened) => {
    if (opened) {
      visible.value = true;
      await nextTick();
      sheetOpen.value = true;
    } else {
      sheetOpen.value = false;
    }
  },
  { immediate: true },
);

function onClosed() {
  visible.value = false;
}

function close() {
  store.closeCashback();
}

function handleSelect(url: string) {
  // Aberto PELO hub, o cashback não navega pra /promotions/<slug>: abre o post
  // EMPILHADO por cima, no PostSheet — o mesmo comportamento dos cards de
  // promoção do hub (ver o case 'blog' em LoyaltyHub.vue). O jogador continua
  // dentro do clube em vez de ser jogado numa rota.
  //
  // A abertura vai pelo param `?open=post&slug=…`, o mecanismo do
  // useLoyaltyHubDeeplink: é ele que decide sessão (deslogado guarda a intenção
  // e abre depois do login) e que apaga o param da URL depois de abrir. Chamar
  // `store.openPost` direto pularia as duas coisas.
  close();
  navigateTo({
    path: route.path,
    query: { ...route.query, open: 'post', slug: cashbackPostSlug(url) },
  });
}
</script>

<template>
  <Teleport to="body">
    <!-- dragstart.prevent por delegação: nada dentro do modal pode ser arrastado
         (o `user-drag` do CSS não cobre Firefox). -->
    <div
      v-if="visible"
      class="lhc-overlay"
      @dragstart.prevent
    >
      <div
        class="lhc-backdrop"
        :class="{ 'lhc-backdrop--out': !sheetOpen }"
        @click="close"
      />

      <Transition
        name="lhc-rise"
        @after-leave="onClosed"
      >
        <div
          v-if="sheetOpen"
          class="lhc-screen"
        >
          <!-- Partículas no rés do chão do painel, atrás de tudo. O painel já é
               `position: relative` e clipa, que é tudo o que a camada precisa. -->
          <BaseParticlesField
            class="lhc-particles"
            :color="ACCENT_COLOR"
            travel="560px"
          />

          <!-- Topbar da roletinha: logo centrada, X à direita. O vão da esquerda
               é um espaçador da largura do X — é o que mantém a logo no centro
               ÓTICO sem posicionamento absoluto. -->
          <header class="lhc-topbar">
            <span
              aria-hidden="true"
              class="lhc-icon"
            />

            <img
              alt="Apostatudo"
              class="lhc-logo"
              :src="logo"
            />

            <button
              aria-label="Fechar"
              class="lhc-icon lhc-close"
              type="button"
              @click="close"
            >
              <svg
                aria-hidden="true"
                fill="none"
                viewBox="0 0 30 30"
              >
                <rect
                  fill="#fff"
                  fill-opacity="0.05"
                  height="30"
                  rx="15"
                  width="30"
                />
                <path
                  d="M19.2784 12.1583L20 11.4399L18.5601 10L17.8417 10.7184L15 13.5601L12.1583 10.7184L11.4399 10L10 11.4399L10.7184 12.1583L13.5601 15L10.7216 17.8417L10 18.5601L11.4399 20L12.1583 19.2816L15 16.4399L17.8417 19.2816L18.5601 20L20 18.5601L19.2816 17.8417L16.4399 15L19.2816 12.1583H19.2784Z"
                  fill="#fff"
                />
              </svg>
            </button>
          </header>

          <p class="lhc-title">{{
            $t('account.wallet.cashback_options_title')
          }}</p>

          <div class="lhc-list">
            <CashbackItem
              v-for="item in items"
              :key="item.name"
              :item="item"
              @select="handleSelect"
            />
          </div>

          <button
            class="lhc-fechar"
            type="button"
            @click="close"
          >
            FECHAR
          </button>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<!-- Entrada sem scope: @keyframes contornam o reset global de `transition`
     (mesma estratégia do hub e da roletinha).
     O card é centrado, então a subida é CURTA (16px + fade) — o slide de 100%
     das folhas coladas no rodapé faria ele atravessar a tela inteira. -->
<style>
.lhc-rise-enter-active {
  animation: lhc-rise 0.28s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.lhc-rise-leave-active {
  animation: lhc-rise 0.2s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  will-change: transform, opacity;
}
@keyframes lhc-rise {
  0% {
    transform: translateY(16px) scale(0.98);
    opacity: 0;
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}
</style>

<style scoped lang="scss">
// z-9995: acima do hub Club Fidelidade (9990) e abaixo do check-in (9996) e da
// roletinha (9999).
// O card fica CENTRADO na tela (nada de colado no rodapé) e o overlay ROLA: em
// aparelho baixo o conteúdo passa da altura da tela, e sem rolagem a parte de
// baixo ficaria fora de alcance. Quem centra é o `margin: auto` do `.lhc-screen`
// — `justify-center` aqui grudaria o card no topo quando ele não coubesse,
// cortando o fim sem como chegar nele.
.lhc-overlay {
  @apply fixed inset-0 z-[9995] flex flex-col select-none;

  padding: 20px 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  img {
    -webkit-user-drag: none;
  }
}

// `fixed` e não `absolute`: o overlay é rolável, e um backdrop absoluto rolaria
// junto com o card (deixando o topo da tela sem véu).
.lhc-backdrop {
  @apply fixed inset-0;

  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
  animation: lhc-fade-in 0.25s ease forwards;
}

.lhc-backdrop--out {
  animation: lhc-fade-in 0.24s ease reverse forwards;
}

@keyframes lhc-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// O painel da roletinha (`.rlt-screen`): mesmo fundo, mesmo recuo interno, mesmo
// raio — só que com os QUATRO cantos arredondados e centrado, em vez de colado
// no rodapé. Sem altura travada: lá ela existe pra composição da roda não
// esticar; aqui o conteúdo é uma lista, que define a própria altura.
// `margin: auto` centra nos dois eixos; passando da tela, o `auto` de cima e de
// baixo vira zero e o overlay rola (comportamento certo do flex).
.lhc-screen {
  @apply relative z-10 flex flex-col w-full;

  // 350px = 318 de card + os 16 de padding de cada lado. O número que importa é
  // o do CARD: no dropdown do menu lateral ele mede 318px (painel de 360, menos
  // px-5 e menos a borda de 1px de cada lado), e é o MESMO componente. Com o
  // painel em 400px o card ia a 368px e a pílula descolava do nome — o card
  // parecia outro nos dois lugares.
  max-width: 350px;
  margin: auto;
  padding: 18px 16px 16px;
  border-radius: 22px;
  background: #000717;
  // `clip` e não `hidden`: `hidden` é scroll container, e aí o ajuste de foco do
  // clique desloca o painel (o bug das bordas da roletinha).
  overflow: clip;
}

// Fora da tela sobra o respiro lateral do overlay — em aparelho estreito o card
// não pode encostar nas bordas, senão os cantos arredondados somem.
@media (max-width: 384px) {
  .lhc-screen {
    width: calc(100% - 32px);
  }
}

// Partículas no chão do painel e o conteúdo um degrau acima: sem
// `position: relative` o conteúdo não participa da ordem de pilha e a camada em
// z-0 pinta por cima dele, por mais que ela venha antes no DOM.
.lhc-particles {
  z-index: 0;
}

.lhc-topbar,
.lhc-title,
.lhc-list,
.lhc-fechar {
  @apply relative z-[1];
}

.lhc-topbar {
  @apply flex items-center justify-between w-full;
}

// Tamanho do X da roletinha (30px). O espaçador da esquerda usa a MESMA classe,
// que é o que equilibra a linha e deixa a logo no centro.
.lhc-icon {
  @apply flex items-center justify-center shrink-0;

  width: 30px;
  height: 30px;
}

.lhc-close svg {
  @apply w-full h-full;
}

.lhc-logo {
  @apply h-[24px] w-auto select-none;
}

.lhc-title {
  @apply text-white text-center font-bold;

  margin-top: 18px;
  font-size: 18px;
  line-height: 26px;
}

.lhc-list {
  display: flex;
  flex-direction: column;
  // 15px é o `gap-[15px]` do dropdown do menu lateral, que mostra esta mesma
  // lista — com 12px o ritmo entre os cards ficava diferente lá e aqui.
  gap: 15px;
  margin-top: 16px;
}

// Fundo translúcido (e não o #010612 chapado de antes) pras partículas passarem
// por trás do botão em vez de morrerem num retângulo opaco.
.lhc-fechar {
  margin-top: 16px;
  width: 100%;
  min-height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  border: 1px solid #1d2329;
  border-radius: 10px;
  background: rgba(1, 6, 18, 0.6);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  backdrop-filter: blur(9px);
}
</style>
